/**
 * Admin Corbeille — /admin/corbeille (V9.12 — Sprint 1 P0 Fiabilité)
 *
 * Vue dédiée aux entités **supprimées doucement** (is_deleted=true) et
 * **archivées** (archived=true). Regroupe les 2 statuts pour une gouvernance
 * unifiée. Actions disponibles pour admin/COPF :
 *   - Restaurer (annule soft_delete / archive → réactive l'entité).
 *   - Purger (admin only) : suppression physique irréversible, bloquée si
 *     relations actives.
 *
 * Toutes les actions sont auditées via `audit_logs`.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { showError } from "@/lib/errors";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  Trash2, RotateCcw, Loader2, ShieldAlert, RefreshCw, Search, ArrowLeft,
  AlertTriangle, Flame, Archive, ExternalLink, Info, History, EyeOff, GitMerge, User as UserIcon,
} from "lucide-react";

// ---------- Constantes ----------
const ENTITIES = [
  { value: "all",                 label: "Toutes" },
  { value: "athlete",             label: "Athlètes" },
  { value: "club",                label: "Clubs" },
  { value: "coach",               label: "Coaches" },
  { value: "federation",          label: "Fédérations" },
  { value: "competition",         label: "Compétitions" },
  { value: "curator_item",        label: "Actualités (curator)" },
  { value: "user_article",        label: "Articles contributeurs" },
  { value: "funding_opportunity", label: "Aides aux sportifs" },
  { value: "calendar_event",      label: "Événements calendrier" },
  { value: "news",                label: "News" },
  { value: "memory_item",         label: "Mémoire" },
  { value: "live",                label: "Lives" },
  { value: "sponsor",             label: "Sponsors" },
];

const ENTITY_LABEL = Object.fromEntries(ENTITIES.map((e) => [e.value, e.label]));

const DETAIL_PATH = {
  athlete:             (it) => it.slug ? `/athletes/${it.slug}` : `/athletes/${it.id}`,
  club:                (it) => it.slug ? `/clubs/${it.slug}` : `/clubs/${it.id}`,
  coach:               (it) => it.slug ? `/coachs/${it.slug}` : `/coachs/${it.id}`,
  federation:          (it) => it.slug ? `/federations/${it.slug}` : `/federations/${it.id}`,
  competition:         (it) => it.slug ? `/competitions/${it.slug}` : `/competitions/${it.id}`,
  curator_item:        () => `/actualites`,
  funding_opportunity: () => `/aides-sportifs`,
  calendar_event:      () => `/calendrier`,
  user_article:        () => `/mes-articles`,
  news:                () => `/actualites`,
  live:                () => `/lives`,
  sponsor:             () => `/sponsors`,
};

// ---------- Page ----------
export default function AdminCorbeillePage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = user && (user.roles || []).some((r) => r === "admin");
  const isModerator = user && (user.roles || []).some((r) => ["admin", "copf"].includes(r));

  // Filtres
  const [tab, setTab] = useState("deleted"); // deleted | archived | unpublished | history
  const [entity, setEntity] = useState("all");
  const [q, setQ] = useState("");
  const [limit] = useState(100);
  const [offset, setOffset] = useState(0);

  // Data
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Dialogs
  const [restoreDialog, setRestoreDialog] = useState(null); // {item}
  const [purgeDialog, setPurgeDialog] = useState(null);     // {item}

  const fetchList = useCallback(async () => {
    if (!isModerator) return;
    setLoading(true);
    try {
      if (tab === "history") {
        // Journal audit — cycle de vie uniquement (hide/archive/soft_delete/restore/purge)
        const params = new URLSearchParams({
          actions_endswith: "hide,archive,soft_delete,restore,purge",
          limit: String(limit),
          offset: String(offset),
        });
        if (entity !== "all") params.set("entity_type", entity);
        const { data } = await api.get(`/admin/audit-logs?${params}`);
        setItems(data?.items || []);
        setTotal(data?.total || 0);
      } else {
        const params = new URLSearchParams({
          entity, status: tab, limit: String(limit), offset: String(offset),
        });
        if (q) params.set("q", q);
        const { data } = await api.get(`/admin/moderation/list?${params}`);
        setItems(data?.items || []);
        setTotal(data?.total || 0);
      }
    } catch (e) {
      showError(e, { prefix: "Corbeille :" });
    } finally { setLoading(false); }
  }, [isModerator, entity, tab, q, limit, offset, refreshKey]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const refresh = () => setRefreshKey((k) => k + 1);

  if (!isModerator) {
    return (
      <div className="max-w-2xl mx-auto p-6" data-testid="corbeille-access-denied">
        <Card className="p-6 text-center">
          <ShieldAlert className="mx-auto h-10 w-10 text-destructive mb-2" />
          <h1 className="text-xl font-semibold">Accès refusé</h1>
          <p className="text-sm text-muted-foreground mt-1">
            La Corbeille est réservée aux administrateurs et à la COPF.
          </p>
          <Button className="mt-4" variant="outline" onClick={() => nav("/")} data-testid="corbeille-back-home">
            <ArrowLeft className="h-4 w-4 mr-1.5" />Retour à l'accueil
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-4" data-testid="admin-corbeille-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-bold flex items-center gap-2">
            <Trash2 className="h-6 w-6 text-destructive" />
            Corbeille administrateur
          </h1>
          <p className="text-sm text-muted-foreground">
            Entités <strong>supprimées doucement</strong> et <strong>archivées</strong>. Vous pouvez les restaurer ou {isAdmin ? <span className="text-destructive font-medium">purger définitivement</span> : "demander leur purge à un admin"}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => nav("/admin/moderation")} data-testid="corbeille-goto-moderation">
            <ExternalLink className="h-3.5 w-3.5 mr-1.5" />Modération
          </Button>
          <Button variant="outline" size="sm" onClick={refresh} data-testid="corbeille-refresh">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />Rafraîchir
          </Button>
        </div>
      </div>

      {/* Tabs statut */}
      <Tabs value={tab} onValueChange={(v) => { setTab(v); setOffset(0); }}>
        <TabsList data-testid="corbeille-tabs">
          <TabsTrigger value="deleted" data-testid="corbeille-tab-deleted">
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />Supprimés
          </TabsTrigger>
          <TabsTrigger value="archived" data-testid="corbeille-tab-archived">
            <Archive className="h-3.5 w-3.5 mr-1.5" />Archivés
          </TabsTrigger>
          <TabsTrigger value="unpublished" data-testid="corbeille-tab-unpublished">
            <EyeOff className="h-3.5 w-3.5 mr-1.5" />Dépubliés
          </TabsTrigger>
          <TabsTrigger value="merged" data-testid="corbeille-tab-merged">
            <GitMerge className="h-3.5 w-3.5 mr-1.5" />Fusionnés
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="corbeille-tab-history">
            <History className="h-3.5 w-3.5 mr-1.5" />Historique
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Filtres */}
      <Card className="p-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
          <div className="space-y-1">
            <Label className="text-xs">Type</Label>
            <Select value={entity} onValueChange={setEntity}>
              <SelectTrigger data-testid="corbeille-filter-entity"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ENTITIES.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label className="text-xs">Recherche (nom / slug)</Label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { setOffset(0); refresh(); } }}
                placeholder={tab === "history" ? "N/A (utilise Type + Actions)" : "Ex : Seasiders, 12h, Jean…"}
                className="pl-8"
                disabled={tab === "history"}
                data-testid="corbeille-filter-q"
              />
            </div>
          </div>
          <Button onClick={() => { setOffset(0); refresh(); }} data-testid="corbeille-apply-filters">
            <Search className="h-4 w-4 mr-1.5" />Appliquer
          </Button>
        </div>
      </Card>

      {/* Info bar */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span data-testid="corbeille-total">
          {total} {tab === "history" ? `entrée${total > 1 ? "s" : ""} d'audit` : `élément${total > 1 ? "s" : ""} en ${tab === "deleted" ? "corbeille" : tab}`}
        </span>
        {tab === "history" ? (
          <span className="flex items-center gap-1.5">
            <Info className="h-3 w-3" />Journal immuable : les purges physiques conservent leur trace ici.
          </span>
        ) : (!isAdmin && (
          <span className="flex items-center gap-1.5">
            <Info className="h-3 w-3" />COPF : action Purger réservée aux admins.
          </span>
        ))}
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        {tab === "history" ? (
          <HistoryTable items={items} loading={loading} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Type</TableHead>
                <TableHead>Nom</TableHead>
                <TableHead className="hidden md:table-cell">Discipline</TableHead>
                <TableHead className="hidden md:table-cell">Owner</TableHead>
                <TableHead className="hidden md:table-cell">Maj</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <div className="space-y-2 p-2">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <Skeleton key={i} className="h-8 w-full" />
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">
                    La corbeille est vide.
                  </TableCell>
                </TableRow>
              )}
              {!loading && items.map((it) => (
                <CorbeilleRow
                  key={`${it.entity}-${it.id}`}
                  item={it}
                  isAdmin={isAdmin}
                  onRestore={() => setRestoreDialog({ item: it })}
                  onPurge={() => setPurgeDialog({ item: it })}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between text-xs">
          <Button variant="outline" size="sm" onClick={() => setOffset(Math.max(0, offset - limit))} disabled={offset === 0} data-testid="corbeille-prev">
            Précédent
          </Button>
          <span>{offset + 1} - {Math.min(offset + limit, total)} sur {total}</span>
          <Button variant="outline" size="sm" onClick={() => setOffset(offset + limit)} disabled={offset + limit >= total} data-testid="corbeille-next">
            Suivant
          </Button>
        </div>
      )}

      {/* Dialogs */}
      {restoreDialog && (
        <RestoreDialog
          open={!!restoreDialog}
          onOpenChange={(v) => { if (!v) setRestoreDialog(null); }}
          item={restoreDialog.item}
          onDone={() => { setRestoreDialog(null); refresh(); }}
        />
      )}
      {purgeDialog && (
        <PurgeDialog
          open={!!purgeDialog}
          onOpenChange={(v) => { if (!v) setPurgeDialog(null); }}
          item={purgeDialog.item}
          onDone={() => { setPurgeDialog(null); refresh(); }}
        />
      )}
    </div>
  );
}

// ---------- Row ----------
function CorbeilleRow({ item, isAdmin, onRestore, onPurge }) {
  const detail = DETAIL_PATH[item.entity]?.(item) || "#";
  const dateMaj = item.updated_at ? new Date(item.updated_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "—";

  return (
    <TableRow data-testid={`corbeille-row-${item.entity}-${item.id}`}>
      <TableCell>
        <Badge variant="outline" className="text-[10px]">{ENTITY_LABEL[item.entity] || item.entity}</Badge>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1.5">
          <a href={detail} target="_blank" rel="noopener noreferrer" className="font-medium truncate max-w-[28ch] inline-block hover:underline" title={item.name} data-testid={`corbeille-name-${item.id}`}>
            {item.name}
          </a>
          <a href={detail} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
        {item.slug && <div className="text-[10px] text-muted-foreground truncate max-w-[28ch]">{item.slug}</div>}
      </TableCell>
      <TableCell className="text-xs hidden md:table-cell">{item.discipline || "—"}</TableCell>
      <TableCell className="text-xs hidden md:table-cell">
        {item.owner_email ? (
          <span className="truncate max-w-[18ch] inline-block" title={item.owner_email}>{item.owner_email}</span>
        ) : "—"}
      </TableCell>
      <TableCell className="text-xs hidden md:table-cell whitespace-nowrap">{dateMaj}</TableCell>
      <TableCell className="text-right">
        <div className="inline-flex items-center gap-1 justify-end">
          {item.status === "merged" ? (
            <>
              <Badge variant="outline" className="text-[10px]" data-testid={`corbeille-merged-badge-${item.id}`}><GitMerge className="h-3 w-3 mr-1" />fusionné</Badge>
              <span className="text-[10px] text-muted-foreground italic ml-1">non restaurable</span>
            </>
          ) : (
            <>
              <Button size="sm" variant="ghost" className="h-7 px-2 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50" onClick={onRestore} data-testid={`corbeille-restore-${item.id}`}>
                <RotateCcw className="h-3.5 w-3.5 mr-1" />Restaurer
              </Button>
              {isAdmin && (
                <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={onPurge} data-testid={`corbeille-purge-${item.id}`}>
                  <Flame className="h-3.5 w-3.5 mr-1" />Purger
                </Button>
              )}
            </>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

// ---------- History (Journal audit) ----------
const ACTION_META = {
  hide:        { label: "Dépublication",  cls: "bg-slate-100 text-slate-700 border-slate-200",  icon: EyeOff },
  archive:     { label: "Archivage",      cls: "bg-amber-100 text-amber-800 border-amber-200",  icon: Archive },
  soft_delete: { label: "Suppression",    cls: "bg-orange-100 text-orange-800 border-orange-200", icon: Trash2 },
  restore:     { label: "Restauration",   cls: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: RotateCcw },
  purge:       { label: "Purge physique", cls: "bg-red-100 text-red-800 border-red-200",        icon: Flame },
};

function parseAction(action) {
  // ex: "athlete.soft_delete" -> {entity: "athlete", suffix: "soft_delete"}
  if (!action || typeof action !== "string" || !action.includes(".")) {
    return { entity: null, suffix: action || "" };
  }
  const idx = action.lastIndexOf(".");
  return { entity: action.slice(0, idx), suffix: action.slice(idx + 1) };
}

function HistoryTable({ items, loading }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-32">Date</TableHead>
          <TableHead className="w-36">Action</TableHead>
          <TableHead className="w-28">Type</TableHead>
          <TableHead>Cible</TableHead>
          <TableHead className="hidden md:table-cell">Auteur</TableHead>
          <TableHead className="hidden lg:table-cell">Motif</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading && (
          <TableRow>
            <TableCell colSpan={6}>
              <div className="space-y-2 p-2">
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
              </div>
            </TableCell>
          </TableRow>
        )}
        {!loading && items.length === 0 && (
          <TableRow>
            <TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">
              Aucune action enregistrée pour ce filtre.
            </TableCell>
          </TableRow>
        )}
        {!loading && items.map((it) => (
          <HistoryRow key={it.id} entry={it} />
        ))}
      </TableBody>
    </Table>
  );
}

function HistoryRow({ entry }) {
  const { entity, suffix } = parseAction(entry.action);
  const meta = ACTION_META[suffix] || { label: suffix || "action", cls: "bg-muted", icon: History };
  const Icon = meta.icon;
  const when = entry.created_at ? new Date(entry.created_at) : null;
  const whenStr = when ? when.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
  const roles = Array.isArray(entry.user_roles) ? entry.user_roles.join(", ") : (entry.user_roles || "—");
  const snapshotName = entry.extra?.snapshot?.name;
  const cascade = entry.extra?.cascade_deleted;

  return (
    <TableRow data-testid={`corbeille-history-row-${entry.id}`}>
      <TableCell className="text-xs whitespace-nowrap">{whenStr}</TableCell>
      <TableCell>
        <Badge variant="outline" className={`text-[10px] ${meta.cls}`}>
          <Icon className="h-3 w-3 mr-1" />{meta.label}
        </Badge>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="text-[10px]">{ENTITY_LABEL[entity] || entity || entry.entity_type || "—"}</Badge>
      </TableCell>
      <TableCell>
        <div className="text-xs">
          {snapshotName ? (
            <span className="font-medium">{snapshotName}</span>
          ) : (
            <span className="text-muted-foreground">{entry.entity_slug || entry.entity_id?.slice(0, 8) || "—"}</span>
          )}
        </div>
        {entry.entity_id && (
          <div className="text-[10px] text-muted-foreground font-mono truncate max-w-[26ch]" title={entry.entity_id}>
            {entry.entity_id}
          </div>
        )}
        {suffix === "purge" && cascade && Object.keys(cascade).length > 0 && (
          <div className="text-[10px] text-red-700 mt-0.5">
            Cascade : {Object.entries(cascade).map(([k, v]) => `${k}: ${v}`).join(" · ")}
          </div>
        )}
      </TableCell>
      <TableCell className="text-xs hidden md:table-cell">
        <div className="flex items-center gap-1">
          <UserIcon className="h-3 w-3 text-muted-foreground" />
          <span className="truncate max-w-[20ch]" title={entry.user_email || entry.user_id}>
            {entry.user_email || entry.user_id?.slice(0, 8) || "—"}
          </span>
        </div>
        {roles !== "—" && <div className="text-[10px] text-muted-foreground">{roles}</div>}
      </TableCell>
      <TableCell className="text-xs hidden lg:table-cell text-muted-foreground">
        <span className="truncate max-w-[24ch] inline-block" title={entry.reason || ""}>
          {entry.reason || "—"}
        </span>
      </TableCell>
    </TableRow>
  );
}

function RestoreDialog({ open, onOpenChange, item, onDone }) {
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/entities/${item.entity}/${item.id}/restore`, {});
      toast.success("Élément restauré.");
      onDone?.();
    } catch (e) { showError(e, { prefix: "Restaurer :" }); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="corbeille-dialog-restore">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><RotateCcw className="h-5 w-5 text-emerald-600" />Restaurer l'élément</DialogTitle>
          <DialogDescription>L'entité redeviendra visible (statut initial). Cette action est journalisée.</DialogDescription>
        </DialogHeader>
        <div className="rounded-md border p-3 bg-muted/30 space-y-1 text-sm">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px]">{ENTITY_LABEL[item.entity] || item.entity}</Badge>
          </div>
          <div className="font-medium">{item.name}</div>
          {item.slug && <div className="text-[11px] text-muted-foreground">{item.slug}</div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="corbeille-restore-cancel">Annuler</Button>
          <Button onClick={submit} disabled={busy} data-testid="corbeille-restore-confirm">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1.5" />}Restaurer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Purge Dialog ----------
function PurgeDialog({ open, onOpenChange, item, onDone }) {
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [relations, setRelations] = useState(null);
  const [relLoading, setRelLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRelLoading(true);
      try {
        const { data } = await api.get(`/admin/entities/${item.entity}/${item.id}/relations`);
        if (!cancelled) setRelations(data);
      } catch (e) { /* silent */ }
      finally { if (!cancelled) setRelLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [item.entity, item.id]);

  const canPurge = useMemo(() => {
    if (busy || relLoading) return false;
    if (!relations) return false;
    if (!relations.can_purge) return false;
    if (!reason.trim()) return false;
    if (confirmText !== "PURGER") return false;
    return true;
  }, [busy, relLoading, relations, reason, confirmText]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/entities/${item.entity}/${item.id}/purge`, null, {
        params: { confirm: "PURGER", reason },
      });
      toast.success("Élément purgé définitivement.");
      onDone?.();
    } catch (e) {
      // Format enrichi si 409 (relations bloquantes)
      const detail = e?.response?.data?.detail;
      if (detail?.error === "purge_blocked_by_relations") {
        setRelations({ ...(relations || {}), blocking: detail.blocking, cascade: detail.cascade, can_purge: false });
        toast.error("Purge bloquée par des relations actives — voir la liste ci-dessous.");
      } else {
        showError(e, { prefix: "Purger :" });
      }
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="corbeille-dialog-purge">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <Flame className="h-5 w-5" />Purger définitivement
          </DialogTitle>
          <DialogDescription>
            <strong>Suppression physique irréversible</strong>. Toutes les données de l'entité sont détruites en base. Cette action est journalisée mais ne peut pas être annulée.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          {/* Identité */}
          <div className="rounded-md border p-3 bg-muted/30 space-y-1">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-[10px]">{ENTITY_LABEL[item.entity] || item.entity}</Badge>
            </div>
            <div className="font-medium">{item.name}</div>
            {item.slug && <div className="text-[11px] text-muted-foreground">{item.slug}</div>}
          </div>

          {/* Relations */}
          <div className="rounded-md border p-3 space-y-2">
            <div className="text-xs font-semibold flex items-center gap-1.5">
              <AlertTriangle className={`h-3.5 w-3.5 ${relations?.can_purge === false ? "text-destructive" : "text-amber-600"}`} />
              Relations existantes
            </div>
            {relLoading && <div className="text-xs text-muted-foreground">Analyse en cours…</div>}
            {!relLoading && relations && (
              <div className="space-y-2">
                {/* Bloquantes */}
                {(relations.blocking || []).length > 0 && (
                  <div>
                    <div className="text-[11px] font-semibold text-destructive mb-1">
                      Bloquantes ({relations.blocking.length}) — à traiter avant purge :
                    </div>
                    <ul className="text-[11px] space-y-1">
                      {relations.blocking.map((b) => (
                        <li key={b.kind} className="flex justify-between border-b border-destructive/20 pb-1">
                          <span className="capitalize">{b.kind.replaceAll("_", " ")}</span>
                          <span className="font-mono font-bold text-destructive">{b.count}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* Cascade */}
                {(relations.cascade || []).length > 0 && (
                  <div>
                    <div className="text-[11px] font-semibold text-amber-700 mb-1">
                      Cascade (supprimés avec l'entité) :
                    </div>
                    <ul className="text-[11px] space-y-1">
                      {relations.cascade.map((c) => (
                        <li key={c.kind} className="flex justify-between border-b border-amber-200 pb-1">
                          <span className="capitalize">{c.kind.replaceAll("_", " ")}</span>
                          <span className="font-mono font-bold text-amber-700">{c.count}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {/* OK */}
                {(!relations.blocking || relations.blocking.length === 0) && (!relations.cascade || relations.cascade.length === 0) && (
                  <div className="text-[11px] text-emerald-700">Aucune relation détectée — purge sûre.</div>
                )}
              </div>
            )}
            {!relLoading && relations?.can_purge === false && (
              <div className="text-[11px] text-destructive pt-1 border-t">
                ⚠️ Purge impossible tant que les relations bloquantes existent. Détachez, révoquez ou archivez ces éléments d'abord.
              </div>
            )}
          </div>

          {/* Raison */}
          <div className="space-y-1">
            <Label className="text-xs">Raison <span className="text-destructive">*</span></Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex : Demande RGPD, spam confirmé, doublon final identifié…"
              rows={2}
              data-testid="corbeille-purge-reason"
            />
          </div>

          {/* Confirmation forte */}
          <div className="space-y-1">
            <Label className="text-xs text-destructive">
              Pour confirmer, tape exactement <code className="bg-muted px-1 rounded">PURGER</code>
            </Label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="PURGER"
              className={confirmText && confirmText !== "PURGER" ? "border-destructive" : ""}
              data-testid="corbeille-purge-confirm-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="corbeille-purge-cancel">Annuler</Button>
          <Button variant="destructive" onClick={submit} disabled={!canPurge} data-testid="corbeille-purge-confirm">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Flame className="h-4 w-4 mr-1.5" />}
            Purger définitivement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
